import type {
  AddStatementLineRequest,
  BankAccountResponse,
  BankOverviewResponse,
  BouncedFlagHistoryRow,
  CashTransferResponse,
  CheckResponse,
  CheckTransitionRequest,
  CreateBankAccountRequest,
  CreateCashTransferRequest,
  CreateCheckRequest,
  ReconciliationResponse,
  SetBouncedFlagRequest,
  SetTicksRequest,
  StartReconciliationRequest,
  UpdateBankAccountRequest,
  UpdateCashTransferRequest,
  UpdateCheckRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, PICKER_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { PARTNERS_KEY } from '@/features/partners/api';
import { throwApiError } from '@/lib/server-messages';

/** The Banks and Checks data layer — same shape as receipts/partners. */

export const BANKING_KEY = ['banking'] as const;
export const CHECKS_KEY = ['checks'] as const;

// ── FIN-09: accounts ────────────────────────────────────────────────────────

export function useBankAccounts() {
  return useQuery({
    queryKey: [...BANKING_KEY, 'accounts'],
    queryFn: async (): Promise<BankAccountResponse[]> => {
      const response = await api.banking.listBankAccounts();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.accounts;
    },
  });
}

export function useCreateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBankAccountRequest): Promise<BankAccountResponse> => {
      const response = await api.banking.createBankAccount({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BANKING_KEY }),
  });
}

export function useUpdateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bankAccountId: string;
      body: UpdateBankAccountRequest;
    }): Promise<BankAccountResponse> => {
      const response = await api.banking.updateBankAccount({
        params: { bankAccountId: input.bankAccountId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BANKING_KEY }),
  });
}

// ── FIN-10 / FIN-11: cheques ────────────────────────────────────────────────

/** FIN-10/FIN-11's register, one page at a time (NFR-42); direction in SQL. */
export function useChecks(
  direction: 'incoming' | 'outgoing',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...CHECKS_KEY, direction, page, pageSize],
    queryFn: async () => {
      const response = await api.banking.listChecks({ query: { direction, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * BR-FIN-10-15's reminders rail, as its OWN query rather than a `.filter()`
 * over the register.
 *
 * It has to be separate now the register is paged: the rail is "every cheque
 * still awaiting action, soonest first", and deriving it from page 1 would
 * silently drop an overdue cheque that happened to sort onto page 2. The
 * server applies the same predicate `lifecycle.ts#awaitingAction` does, and
 * orders by cheque date; outstanding cheques are a small set by definition,
 * so one page of the ceiling covers it.
 */
/**
 * PV-01's endorsed-cheque picker: the customer cheques still IN HAND, matched
 * on the printed number, the issuing bank or the customer's name — a SEARCH
 * the server narrows (GEN-50), never a filter over one page of the register.
 */
export function useInHandCheques(search: string) {
  return useQuery({
    queryKey: [...CHECKS_KEY, 'in-hand', search],
    queryFn: async () => {
      const response = await api.banking.listChecks({
        query: {
          direction: 'incoming',
          status: 'in_hand',
          ...(search.trim() ? { q: search.trim() } : {}),
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.checks;
    },
    placeholderData: keepPreviousData,
  });
}

export function useChecksAwaitingAction(direction: 'incoming' | 'outgoing') {
  return useQuery({
    queryKey: [...CHECKS_KEY, direction, 'awaiting'],
    queryFn: async () => {
      const response = await api.banking.listChecks({
        query: { direction, awaitingAction: 'true', page: 1, pageSize: MAX_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

function useCheckMutation<TInput>(
  run: (input: TInput) => Promise<CheckResponse | { check: CheckResponse; message: string }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
      void queryClient.invalidateQueries({ queryKey: BANKING_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}

export function useCreateCheck() {
  return useCheckMutation(async (body: CreateCheckRequest) => {
    const response = await api.banking.createCheck({ body });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useUpdateCheck() {
  return useCheckMutation(async (input: { checkId: string; body: UpdateCheckRequest }) => {
    const response = await api.banking.updateCheck({
      params: { checkId: input.checkId },
      body: input.body,
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useTransitionCheck() {
  return useCheckMutation(async (input: { checkId: string; body: CheckTransitionRequest }) => {
    const response = await api.banking.transitionCheck({
      params: { checkId: input.checkId },
      body: input.body,
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useVoidCheck() {
  return useCheckMutation(async (input: { checkId: string; reason: string }) => {
    const response = await api.banking.voidCheck({
      params: { checkId: input.checkId },
      body: { reason: input.reason },
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useReverseCheck() {
  return useCheckMutation(async (checkId: string) => {
    const response = await api.banking.reverseCheck({ params: { checkId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useReissueCheck() {
  return useCheckMutation(async (checkId: string) => {
    const response = await api.banking.reissueCheck({ params: { checkId }, body: {} });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

// ── R10: the bounced-cheque flag ────────────────────────────────────────────

export function useSetBouncedFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { partnerId: string; body: SetBouncedFlagRequest }) => {
      const response = await api.banking.setBouncedFlag({
        params: { partnerId: input.partnerId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHECKS_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}

export function useBouncedFlagHistory(partnerId: string | null) {
  return useQuery({
    queryKey: [...CHECKS_KEY, 'flag-history', partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<BouncedFlagHistoryRow[]> => {
      const response = await api.banking.bouncedFlagHistory({ params: { partnerId: partnerId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.history;
    },
  });
}

// ── FIN-09: reconciliation ──────────────────────────────────────────────────

/**
 * BR-FIN-09-13's history for one account, one page at a time (NFR-42).
 *
 * `status` is a server-side filter rather than a `.filter()` on the result,
 * because the screen asks two different questions of this list — "is there an
 * open period to continue?" and "show the completed ones" — and once the list
 * is paged, an answer derived from page 1 is not an answer.
 */
export function useReconciliationHistory(
  bankAccountId: string | null,
  status?: 'in_progress' | 'completed',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...BANKING_KEY, 'reconciliations', bankAccountId, status ?? 'any', page, pageSize],
    enabled: Boolean(bankAccountId),
    queryFn: async () => {
      const response = await api.banking.listReconciliations({
        query: {
          bankAccountId: bankAccountId!,
          ...(status ? { status } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useReconciliation(reconciliationId: string | null) {
  return useQuery({
    queryKey: [...BANKING_KEY, 'reconciliation', reconciliationId],
    enabled: Boolean(reconciliationId),
    queryFn: async (): Promise<ReconciliationResponse> => {
      const response = await api.banking.getReconciliation({
        params: { reconciliationId: reconciliationId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useReconMutation<TInput, TOut>(run: (input: TInput) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: BANKING_KEY }),
  });
}

export function useStartReconciliation() {
  return useReconMutation(async (body: StartReconciliationRequest) => {
    const response = await api.banking.startReconciliation({ body });
    if (response.status !== 200 && response.status !== 201) {
      throwApiError(response.status, response.body);
    }
    return response.body;
  });
}

export function useSetStatement() {
  return useReconMutation(
    async (input: { reconciliationId: string; statementBalance: string }) => {
      const response = await api.banking.setStatement({
        params: { reconciliationId: input.reconciliationId },
        body: { statementBalance: input.statementBalance },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useSetTicks() {
  return useReconMutation(async (input: { reconciliationId: string; body: SetTicksRequest }) => {
    const response = await api.banking.setTicks({
      params: { reconciliationId: input.reconciliationId },
      body: input.body,
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useAddStatementLine() {
  return useReconMutation(
    async (input: { reconciliationId: string; body: AddStatementLineRequest }) => {
      const response = await api.banking.addStatementLine({
        params: { reconciliationId: input.reconciliationId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useAddAdjustment() {
  return useReconMutation(async (reconciliationId: string) => {
    const response = await api.banking.addAdjustment({ params: { reconciliationId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useCompleteReconciliation() {
  return useReconMutation(async (reconciliationId: string) => {
    const response = await api.banking.completeReconciliation({
      params: { reconciliationId },
      body: {},
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useReopenReconciliation() {
  return useReconMutation(async (reconciliationId: string) => {
    const response = await api.banking.reopenReconciliation({
      params: { reconciliationId },
      body: {},
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

// ── FIN-06: cash transfers ──────────────────────────────────────────────────

/** FIN-06's list, one page at a time (NFR-42). */
export function useCashTransfers(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...BANKING_KEY, 'transfers', page, pageSize],
    queryFn: async () => {
      const response = await api.banking.listCashTransfers({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useCashTransfer(transferId: string | undefined) {
  return useQuery({
    queryKey: [...BANKING_KEY, 'transfer', transferId],
    enabled: Boolean(transferId),
    queryFn: async (): Promise<CashTransferResponse> => {
      const response = await api.banking.getCashTransfer({ params: { transferId: transferId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateCashTransfer() {
  return useReconMutation(async (body: CreateCashTransferRequest) => {
    const response = await api.banking.createCashTransfer({ body });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useUpdateCashTransfer() {
  return useReconMutation(
    async (input: { transferId: string; body: UpdateCashTransferRequest }) => {
      const response = await api.banking.updateCashTransfer({
        params: { transferId: input.transferId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useDeleteCashTransfer() {
  return useReconMutation(async (transferId: string) => {
    const response = await api.banking.removeCashTransfer({ params: { transferId } });
    if (response.status !== 204) throwApiError(response.status, response.body);
  });
}

export function useCancelCashTransfer() {
  // GEN-59: the typed reason travels in the body — the contract refuses an
  // empty one, and the service stores it on the row and the audit row.
  return useReconMutation(async ({ transferId, reason }: { transferId: string; reason: string }) => {
    const response = await api.banking.cancelCashTransfer({
      params: { transferId },
      body: { reason },
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function usePostCashTransfer() {
  return useReconMutation(async (transferId: string) => {
    const response = await api.banking.postCashTransfer({ params: { transferId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

// ── FIN-12: the morning page ────────────────────────────────────────────────

export function useBankOverview(range: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: [...BANKING_KEY, 'overview', range],
    queryFn: async (): Promise<BankOverviewResponse> => {
      const response = await api.banking.overview({
        query: {
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}
